/**
 * Demo route table — maps the agency REST endpoints the toured screens call to
 * the simulated data in demo-data. Registered on import (side-effect).
 *
 * Proposals are kept in a mutable list so approving/denying in the Action Queue
 * (or the dashboard card) actually removes the row on the next refetch — the
 * interaction feels like the live product.
 */
import { registerDemoRoutes } from './demo-fixtures';
import {
  DEMO_ACTION_RULES,
  DEMO_ALL_ACTIVATED_ITEMS,
  DEMO_AUDIT,
  DEMO_AUDIT_META,
  DEMO_DELIVERY_OPTIONS_ACTIVE,
  DEMO_EMPLOYEES,
  DEMO_GROUPS,
  DEMO_IDENTITY,
  DEMO_INSIGHTS_AVAILABLE,
  DEMO_INSIGHT_PREFERENCES,
  DEMO_MCP_AVAILABLE,
  DEMO_MCP_CONNECTIONS,
  DEMO_MCP_CREDENTIALS,
  DEMO_META_ACTIVITY,
  DEMO_META_META,
  DEMO_META_SUMMARY,
  DEMO_ORG_SKILLS,
  DEMO_PRESENTATION_FREQUENCIES,
  DEMO_PROPOSALS_SEED,
  DEMO_ROLES,
  DEMO_SELLER_INIT,
  DEMO_MCP_CALLS,
  DEMO_MCP_CALLS_META,
  DEMO_SKILLS,
  perUserForWindow,
  statsForWindow,
  toolAccessFor,
  DEMO_REPORT_DETAIL,
  DEMO_REPORTS_LIST,
  nextReportVersion,
  listMetrics,
  createMetric,
  listKpiTargets,
  upsertKpiTarget,
  DEMO_REPORT_STYLE_PRESETS,
  DEMO_REPORT_BRANDING,
  DEMO_CLIENT_PORTAL_SETTINGS,
  DEMO_AGENCY_DOMAINS,
} from './demo-data';
import { DEMO_REPORT_HTML } from './demo-report-html';

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

// Mutable proposal state (approve/deny mutate it; reset on tour restart).
let proposals: any[] = DEMO_PROPOSALS_SEED.map((p) => ({ ...p }));
export const resetDemoProposals = (): void => {
  proposals = DEMO_PROPOSALS_SEED.map((p) => ({ ...p }));
};

const reviewedNow = () => new Date().toISOString();

/* -------- GDPR filter settings (agency-wide, mutable in-tour) -------- */

const GDPR_DEMO_CATEGORIES = [
  { key: 'email', label: 'E-mail addresses' },
  { key: 'phone', label: 'Phone numbers' },
  { key: 'name', label: 'Person names' },
  { key: 'address', label: 'Physical addresses' },
  { key: 'ip_address', label: 'IP addresses' },
  { key: 'id_numbers', label: 'ID numbers (SSN, passport, tax/VAT)' },
  { key: 'user_ids', label: 'End-user / customer identifiers' },
  { key: 'dob', label: 'Birthdates' },
  { key: 'location', label: 'Precise location (lat/lng)' },
  { key: 'financial', label: 'Financial data (IBAN, card numbers)' },
  { key: 'message_content', label: 'Message / conversation content' },
  { key: 'audience_data', label: 'Audience / customer-list data' },
];
const GDPR_ALL_KEYS = GDPR_DEMO_CATEGORIES.map((c) => c.key);
const gdprDefaultPlatform = (key: string, label: string, connectedBrands: number) => ({
  platform_key: key,
  label,
  connected: connectedBrands > 0,
  connected_brands: connectedBrands,
  enabled: true,
  blocked_categories: [...GDPR_ALL_KEYS],
  is_customised: false,
  updated_at: null as string | null,
});
const gdprPlatforms = [
  gdprDefaultPlatform('facebook', 'Facebook Ads', 3),
  gdprDefaultPlatform('google_ads', 'Google Ads', 3),
  gdprDefaultPlatform('ga4', 'Google Analytics 4', 1),
  gdprDefaultPlatform('intercom', 'Intercom', 1),
  gdprDefaultPlatform('hubspot', 'HubSpot', 0),
  gdprDefaultPlatform('shopify', 'Shopify', 0),
  gdprDefaultPlatform('custom', 'Custom connectors', 0),
];

registerDemoRoutes([
  /* -------- bootstrap -------- */
  { method: 'GET', test: /\/api\/v1\/session$/, handler: () => ({ success: false }) },
  { method: 'GET', test: /\/api\/v1\/me$/, handler: () => clone(DEMO_IDENTITY) },
  { method: 'GET', test: /\/api\/v1\/seller-init$/, handler: () => clone(DEMO_SELLER_INIT) },

  /* -------- proposals (v2) -------- */
  {
    method: 'GET',
    test: /\/api\/v2\/employee\/tara\/proposals$/,
    handler: ({ query }) => {
      const status = query.get('status');
      const requestedBy = query.get('requested_by_employee_id');
      const shopSlug = query.get('shop_slug');
      const platform = query.get('platform');
      let list = proposals.slice();
      if (status) list = list.filter((p) => p.status === status);
      if (requestedBy) list = list.filter((p) => p.requested_by_employee_id === requestedBy);
      if (shopSlug) list = list.filter((p) => p.shop?.slug === shopSlug);
      if (platform) list = list.filter((p) => p.platform === platform);
      const perPage = Number(query.get('per_page')) || 20;
      const page = Number(query.get('page')) || 1;
      const start = (page - 1) * perPage;
      return {
        data: clone(list.slice(start, start + perPage)),
        meta: {
          total: list.length,
          per_page: perPage,
          current_page: page,
          last_page: Math.max(1, Math.ceil(list.length / perPage)),
        },
      };
    },
  },
  {
    method: 'POST',
    test: /\/api\/v2\/employee\/tara\/proposals\/([^/]+)\/approve$/,
    handler: ({ match }) => {
      const p = proposals.find((x) => x.id === match[1]);
      if (p) {
        p.status = 'approved';
        p.auto_approved = false;
        p.reviewed_at = reviewedNow();
      }
      return { status: 'approved', reviewed_at: reviewedNow() };
    },
  },
  {
    method: 'POST',
    test: /\/api\/v2\/employee\/tara\/proposals\/([^/]+)\/deny$/,
    handler: ({ match }) => {
      const p = proposals.find((x) => x.id === match[1]);
      if (p) {
        p.status = 'denied';
        p.reviewed_at = reviewedNow();
      }
      return { status: 'denied', reviewed_at: reviewedNow() };
    },
  },
  {
    method: 'GET',
    test: /\/api\/v2\/employee\/tara\/proposals\/([^/]+)$/,
    handler: ({ match }) => {
      const p = proposals.find((x) => x.id === match[1]);
      return p ? clone(p) : {};
    },
  },

  /* -------- stats (v1) -------- */
  { method: 'GET', test: /\/api\/v1\/agency\/stats$/, handler: ({ query }) => statsForWindow(query.get('window') || '30d') },
  { method: 'GET', test: /\/api\/v1\/agency\/stats\/per-user$/, handler: ({ query }) => perUserForWindow(query.get('window') || '30d') },
  { method: 'GET', test: /\/api\/v1\/agency\/mcp-calls$/, handler: ({ query }) => ({ data: clone(DEMO_MCP_CALLS), meta: { ...DEMO_MCP_CALLS_META, current_page: Number(query.get('page')) || 1 } }) },
  { method: 'GET', test: /\/api\/v1\/shops\/[^/]+\/tool-access$/, handler: ({ query }) => toolAccessFor(query.get('employee')) },

  /* -------- client-report portal (v1) — bespoke fetch, bare {data} -------- */
  // The full report HTML is served RAW as text/html (via the __rawHtml marker,
  // see demo-backend) so the sandboxed viewer iframe renders it, not JSON.
  // Honour ?shop_slug= — the Reports tab is brand-scoped, so an unfiltered
  // mock would show another client's reports under the active brand.
  {
    method: 'GET',
    test: /\/api\/v1\/client-reports$/,
    handler: ({ query }) => {
      const slug = query.get('shop_slug');
      const rows = clone(DEMO_REPORTS_LIST);
      return { data: slug ? rows.filter((r: any) => r.shop_slug === slug) : rows };
    },
  },
  { method: 'GET', test: /\/api\/v1\/client-reports\/[^/]+\/html$/, handler: () => ({ __rawHtml: DEMO_REPORT_HTML }) },
  { method: 'POST', test: /\/api\/v1\/client-reports\/[^/]+\/versions$/, handler: () => nextReportVersion() },
  { method: 'GET', test: /\/api\/v1\/client-reports\/[^/]+$/, handler: () => ({ data: clone(DEMO_REPORT_DETAIL) }) },
  { method: 'GET', test: /\/api\/v1\/metrics$/, handler: () => ({ data: listMetrics() }) },
  { method: 'POST', test: /\/api\/v1\/metrics$/, handler: ({ body }) => ({ data: createMetric((body ?? {}) as Record<string, unknown>) }) },
  { method: 'GET', test: /\/api\/v1\/shops\/[^/]+\/kpi-targets$/, handler: () => ({ data: listKpiTargets() }) },
  { method: 'POST', test: /\/api\/v1\/shops\/[^/]+\/kpi-targets$/, handler: ({ body }) => ({ data: upsertKpiTarget((body ?? {}) as Record<string, unknown>) }) },
  { method: 'PUT', test: /\/api\/v1\/shops\/[^/]+\/kpi-targets\/([^/]+)$/, handler: ({ match, body }) => ({ data: upsertKpiTarget({ id: match[1], ...((body ?? {}) as Record<string, unknown>) }) }) },
  { method: 'DELETE', test: /\/api\/v1\/shops\/[^/]+\/kpi-targets\/[^/]+$/, handler: () => ({ data: {} }) },
  { method: 'GET', test: /\/api\/v1\/report-style-presets$/, handler: () => ({ data: clone(DEMO_REPORT_STYLE_PRESETS) }) },
  { method: 'GET', test: /\/api\/v1\/report-branding$/, handler: () => ({ data: clone(DEMO_REPORT_BRANDING) }) },
  { method: 'GET', test: /\/api\/v1\/shops\/[^/]+\/client-portal-settings$/, handler: () => ({ data: clone(DEMO_CLIENT_PORTAL_SETTINGS) }) },
  { method: 'PUT', test: /\/api\/v1\/shops\/[^/]+\/client-portal-settings$/, handler: ({ body }) => ({ data: { ...clone(DEMO_CLIENT_PORTAL_SETTINGS), ...((body ?? {}) as object) } }) },
  { method: 'GET', test: /\/api\/v1\/agency-domains$/, handler: () => ({ data: clone(DEMO_AGENCY_DOMAINS) }) },

  /* -------- GDPR filter settings (v1, agency-wide) -------- */
  {
    method: 'GET',
    test: /\/api\/v1\/agency\/gdpr-settings$/,
    handler: () => ({
      categories: clone(GDPR_DEMO_CATEGORIES),
      default: { enabled: true, blocked_categories: [...GDPR_ALL_KEYS] },
      platforms: clone(gdprPlatforms),
    }),
  },
  {
    method: 'PUT',
    test: /\/api\/v1\/agency\/gdpr-settings\/([^/]+)$/,
    handler: ({ match, body }) => {
      const key = decodeURIComponent(match[1]);
      const next = (body ?? {}) as { enabled?: boolean; blocked_categories?: string[] };
      const row = gdprPlatforms.find((p) => p.platform_key === key);
      if (row) {
        row.enabled = next.enabled !== false;
        row.blocked_categories = Array.isArray(next.blocked_categories)
          ? next.blocked_categories.filter((c) => GDPR_ALL_KEYS.includes(c))
          : row.blocked_categories;
        row.is_customised = true;
        row.updated_at = reviewedNow();
        return clone(row);
      }
      return {
        platform_key: key,
        enabled: next.enabled !== false,
        blocked_categories: next.blocked_categories ?? [],
        is_customised: true,
        updated_at: reviewedNow(),
      };
    },
  },
  {
    method: 'DELETE',
    test: /\/api\/v1\/agency\/gdpr-settings\/([^/]+)$/,
    handler: ({ match }) => {
      const key = decodeURIComponent(match[1]);
      const row = gdprPlatforms.find((p) => p.platform_key === key);
      if (row) {
        row.enabled = true;
        row.blocked_categories = [...GDPR_ALL_KEYS];
        row.is_customised = false;
        row.updated_at = null;
        return clone(row);
      }
      return {
        platform_key: key,
        enabled: true,
        blocked_categories: [...GDPR_ALL_KEYS],
        is_customised: false,
      };
    },
  },

  /* -------- control center / governance (v1) -------- */
  {
    method: 'GET',
    test: /\/api\/v1\/shops\/[^/]+\/mcp-connections$/,
    handler: () => clone(DEMO_MCP_CONNECTIONS),
  },
  {
    method: 'PATCH',
    test: /\/api\/v1\/shops\/[^/]+\/mcp-connections\/[^/]+\/tools\/([^/]+)$/,
    handler: ({ match, body }) => {
      const tool = decodeURIComponent(match[1]);
      const policy = (body as { policy?: string } | undefined)?.policy ?? 'allow';
      return { tool_name: tool, classification: 'write', policy, description: '' };
    },
  },
  // Integrations grid — available platforms + their connections. BARE
  // {success,data} envelope (asyncHandler clients). `is_pending=true` -> none.
  {
    method: 'GET',
    test: /\/api\/v1\/tara\/mcp\/index$/,
    handler: ({ query }) => ({
      success: true,
      data: query.get('is_pending') === 'true' ? [] : clone(DEMO_MCP_AVAILABLE),
    }),
  },
  {
    method: 'GET',
    test: /\/api\/v1\/tara\/mcp\/[^/]+\/credentials$/,
    handler: () => ({ success: true, data: clone(DEMO_MCP_CREDENTIALS) }),
  },
  // subscription packages (billing upgrade path) — empty list, valid envelope
  { method: 'GET', test: /\/api\/v1\/subscriptions\/subscription-types\/[^/]+\/packages$/, handler: () => ({ success: true, data: [] }) },

  /* -------- Meta Activity log (v1) -------- */
  { method: 'GET', test: /\/api\/v1\/agency\/meta-activity\/summary$/, handler: () => clone(DEMO_META_SUMMARY) },
  {
    method: 'GET',
    test: /\/api\/v1\/agency\/meta-activity$/,
    handler: ({ query }) => ({
      data: clone(DEMO_META_ACTIVITY),
      meta: { ...DEMO_META_META, current_page: Number(query.get('page')) || 1 },
    }),
  },

  /* -------- Skills (v1) -------- */
  { method: 'GET', test: /\/api\/v1\/skills$/, handler: () => clone(DEMO_SKILLS) },
  { method: 'GET', test: /\/api\/v1\/org\/skills$/, handler: () => clone(DEMO_ORG_SKILLS) },

  /* -------- Notifications / reports (asyncHandler -> need success:true) -------- */
  { method: 'GET', test: /\/api\/v2\/presentations\/frequencies$/, handler: () => ({ success: true, data: clone(DEMO_PRESENTATION_FREQUENCIES) }) },
  { method: 'GET', test: /\/api\/v1\/tara\/shops\/[^/]+\/insights\/available$/, handler: () => ({ success: true, data: clone(DEMO_INSIGHTS_AVAILABLE) }) },
  { method: 'GET', test: /\/api\/v2\/shops\/[^/]+\/custom-graphs$/, handler: () => ({ success: true, data: [] }) },
  { method: 'GET', test: /\/api\/v2\/shops\/[^/]+\/custom-prompts$/, handler: () => ({ success: true, data: [] }) },
  { method: 'GET', test: /\/api\/v1\/tara\/shops\/[^/]+\/insights\/all-activated-items$/, handler: () => ({ success: true, data: clone(DEMO_ALL_ACTIVATED_ITEMS) }) },
  { method: 'GET', test: /\/api\/v1\/tara\/shops\/[^/]+\/insight-preferences$/, handler: () => ({ success: true, data: clone(DEMO_INSIGHT_PREFERENCES) }) },
  { method: 'GET', test: /\/api\/v1\/tara\/shops\/[^/]+\/delivery-options\/active$/, handler: () => ({ success: true, data: clone(DEMO_DELIVERY_OPTIONS_ACTIVE) }) },

  /* -------- team (v1) -------- */
  { method: 'GET', test: /\/api\/v1\/agency\/employees$/, handler: () => clone(DEMO_EMPLOYEES) },
  { method: 'GET', test: /\/api\/v1\/agency\/groups$/, handler: () => clone(DEMO_GROUPS) },
  { method: 'GET', test: /\/api\/v1\/agency\/roles$/, handler: () => clone(DEMO_ROLES) },

  /* -------- audit (v1) -------- */
  {
    method: 'GET',
    test: /\/api\/v1\/agency\/audit-log$/,
    handler: ({ query }) => ({
      data: clone(DEMO_AUDIT),
      meta: { ...DEMO_AUDIT_META, current_page: Number(query.get('page')) || 1 },
    }),
  },

  /* -------- legacy action rules (seller token) -------- */
  {
    method: 'GET',
    test: /\/api\/v1\/shops\/[^/]+\/action-rules$/,
    handler: () => ({ success: true, message: 'ok', data: clone(DEMO_ACTION_RULES) }),
  },
]);
